import { useTranslation } from 'react-i18next'
import {
	BadgeCheckIcon, AlertCircleIcon, FileTextIcon,
	BriefcaseIcon, ClipboardListIcon, MessageSquareIcon, BellIcon,
} from 'lucide-react'
import {
	NotificationCard, NotificationPanel,
	type NotificationCardVariant,
} from '@rentflowdev/ui'
import {
	useDeleteNotificationMutation,
	useListNotificationsQuery,
	useMarkAllAsReadMutation,
	useMarkAsReadMutation,
} from '@/api'
import type {
	Notification,
	NotificationType,
} from '@rentflowdev/api'

const ICON_FOR: Record<NotificationType, React.ReactNode> = {
	CONTRACT_SIGNED:     <BadgeCheckIcon    className="size-5" />,
	LIMIT_REACHED:       <AlertCircleIcon   className="size-5" />,
	INVOICE_GENERATED:   <FileTextIcon      className="size-5" />,
	NEW_DEAL:            <BriefcaseIcon     className="size-5" />,
	NEW_TASK:            <ClipboardListIcon className="size-5" />,
	NEW_MESSAGE:         <MessageSquareIcon className="size-5" />,
	UNPUBLISHED_CHANGES: <AlertCircleIcon   className="size-5" />,
}

const DEFAULT_VARIANT_FOR: Record<NotificationType, NotificationCardVariant> = {
	CONTRACT_SIGNED:     'success',
	LIMIT_REACHED:       'warning',
	INVOICE_GENERATED:   'default',
	NEW_DEAL:            'default',
	NEW_TASK:            'default',
	NEW_MESSAGE:         'default',
	UNPUBLISHED_CHANGES: 'warning',
}

export function NotificationsDropdown() {
	const { t } = useTranslation()
	const { data, isLoading } = useListNotificationsQuery()
	const [markAsRead]         = useMarkAsReadMutation()
	const [markAllAsRead]      = useMarkAllAsReadMutation()
	const [deleteNotification] = useDeleteNotificationMutation()

	const notifications = data?.notifications ?? []
	const unreadCount   = data?.unreadCount   ?? 0

	const empty = (
		<div className="flex flex-col items-center gap-2 py-10 text-(--color-muted-foreground)">
			<BellIcon className="size-6" />
			<p className="text-sm">
				{isLoading ? 'Загрузка…' : 'Нет новых уведомлений'}
			</p>
		</div>
	)

	return (
		<NotificationPanel
			title={t('common.notifications')}
			markAllLabel={t('common.markAllRead')}
			count={unreadCount}
			onMarkAllRead={notifications.length > 0 ? () => void markAllAsRead() : undefined}
			emptyState={empty}
		>
			{notifications.map((n: Notification) => (
				<NotificationCard
					key={n.id}
					icon={ICON_FOR[n.type]}
					variant={n.variant ?? DEFAULT_VARIANT_FOR[n.type]}
					title={n.title}
					body={n.body}
					unread={!n.isRead}
					onClose={() => void deleteNotification(n.id)}
					actions={n.actions?.map((a) => ({
						label:   a.label,
						href:    a.href,
						onClick: a.actionKey ? () => void markAsRead(n.id) : undefined,
					}))}
				/>
			))}
		</NotificationPanel>
	)
}
